import React, { useState, useContext, useRef, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { ModernLayoutProvider } from "../../context/ModernLayout/ModernLayoutContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import api from "../../services/api";
import TicketsListCustom from "../../components/TicketsListCustom";
import Ticket from "../../components/Ticket";
import ContactDrawer from "../../components/ContactDrawer";
import useTicketCounts from "../../hooks/useTicketCounts";
import {
  MessageSquare,
  Clock,
  Users,
  Search,
  Archive,
  PlusCircle,
  Eye,
  EyeOff,
  Filter,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  FileText,
  File,
  Zap,
  BarChart2,
  GitBranch,
  ShieldCheck,
} from "lucide-react";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import { UsersFilter } from "../../components/UsersFilter";
import { StatusFilter } from "../../components/StatusFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import TicketsQueueSelect from "../../components/TicketsQueueSelect";
import { Can } from "../../components/Can";
import CloseAllTicketsModal from "../../components/CloseAllTicketsModal";
import { Tooltip, Typography, Button, Collapse, Tabs, Tab, Badge, Grid, IconButton } from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";
import MessageSharpIcon from "@material-ui/icons/MessageSharp";
import ClockIcon from "@material-ui/icons/AccessTime";
import GroupIcon from "@material-ui/icons/Group";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    flex: 1,
    padding: theme.spacing(0),
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
  },
  chatPapper: {
    display: "flex",
    height: "100%",
    margin: theme.spacing(0),
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(0.5),
      height: "calc(100% - 8px)",
    },
  },
  chatPapperWithDrawer: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  contactsWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "row",
    overflowY: "hidden",
    position: "relative",
    borderRadius: "12px 0 0 12px",
    backgroundColor: "transparent",
    width: "45%",
    maxWidth: "420px",
    minWidth: "300px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "none",
    },
  },
  sidebar: {
    width: "50px",
    minWidth: "50px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    position: "relative",
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  sidebarButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    marginTop: theme.spacing(2),
    overflow: "visible",
  },
  sidebarButton: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    color: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.divider}`,
    position: "relative",
    overflow: "visible",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#FFF",
    },
  },
  sidebarAddButton: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 14,
    marginBottom: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  ticketsSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.default,
    overflow: "hidden",
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  controlsBar: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.14, 1.3),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  controlsTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  controlButton: {
    width: "28px",
    height: "28px",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    "&.active": {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.primary.main,
    },
  },
  queueSelect: {
    flex: 1,
    marginTop: theme.spacing(0.5),
  },
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    padding: theme.spacing(0.5, 1),
    marginTop: theme.spacing(1),
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "none",
    outline: "none",
    padding: theme.spacing(0.5),
    color: theme.palette.text.primary,
    fontSize: "14px",
    "&::placeholder": {
      color: theme.palette.text.secondary,
    },
  },
  controlButtons: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },
  badgeCount: {
    position: "absolute",
    top: "-4px",
    right: "-4px",
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    borderRadius: "50%",
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: "bold",
    zIndex: 10,
  },
  // Mobile Tabs Styles (igual layout antigo)
  mobileTabs: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      minHeight: 54,
      borderRadius: 8,
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.02)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      padding: "8px 4px 4px 4px",
      margin: "8px 0",
      transition: "all 0.3s ease",
      position: "relative",
    },
  },
  mobileTabPanelItem: {
    minWidth: "33%",
    fontSize: 11,
    marginLeft: 0,
    transition: "all 0.3s ease",
    fontWeight: 500,
    borderRadius: "8px",
    margin: "0 4px",
    opacity: 0.7,
    "&.Mui-selected": {
      opacity: 1,
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    },
    "&:hover": {
      opacity: 1,
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.05)",
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: "30%",
      fontSize: 9,
      padding: theme.spacing(0.5),
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: "28%",
      fontSize: 8,
    },
  },
  mobileTabIndicator: {
    height: 3,
    bottom: 0,
    borderRadius: "3px 3px 0 0",
    backgroundColor: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    transition: "all 0.3s ease",
  },
  mobileCustomBadge: {
    right: "-12px",
    top: "-10px",
    transform: "translate(30%, -30%)",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "10px",
    padding: "0 6px",
    fontWeight: "bold",
    fontSize: "0.75rem",
    minWidth: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
  },
  mobileCustomBadgeRed: {
    right: "-12px",
    top: "-10px",
    transform: "translate(30%, -30%)",
    backgroundColor: "#f44336",
    color: "#fff",
    borderRadius: "10px",
    padding: "0 6px",
    fontWeight: "bold",
    fontSize: "0.75rem",
    minWidth: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
  },
  mobileSidebarBar: {
    display: "none",
    "@media (max-width: 600px)": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1),
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  mobileSidebarButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    minHeight: 36,
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.divider}`,
    position: "relative",
    flexShrink: 0,
    "&.active": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  mobileControlsBar: {
    display: "none",
    "@media (max-width: 600px)": {
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(1),
      gap: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  mobileControlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(1),
  },
  mobileControlButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
    "& > *": {
      flexShrink: 0,
    },
  },
  flowControlButton: {
    minWidth: 28,
    minHeight: 28,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  desktopControlsBar: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    flexGrow: 1,
    borderRadius: "0 12px 12px 0",
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    position: "relative",
    transition: theme.transitions.create(["width", "border-radius"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  messagesWrapperWithDrawer: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  messagesWrapperHidden: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  welcomeMsg: {
    background: "transparent",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    margin: 0,
    height: "100%",
    width: "100%",
    textAlign: "center",
    borderRadius: 0,
    boxShadow: "none",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    overflow: "auto",
  },
  welcomeIcon: {
    marginBottom: theme.spacing(3),
    width: "120px",
    height: "120px",
    color: theme.palette.primary.main,
    opacity: 0.2,
  },
  welcomeTitle: {
    fontSize: "1.75rem",
    fontWeight: "600",
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  welcomeSubtitle: {
    fontSize: "1rem",
    maxWidth: "450px",
    lineHeight: "1.5",
    marginBottom: theme.spacing(4),
  },
  welcomeDescription: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    maxWidth: "750px",
    margin: theme.spacing(2, "auto"),
    lineHeight: 1.6,
  },
  logo: {
    width: "300px",
    height: "auto",
    marginBottom: theme.spacing(1),
    content: "url(" + (theme.mode === "light" 
      ? theme.calculatedLogoLight() 
      : theme.calculatedLogoDark()) + ")",
    [theme.breakpoints.down("sm")]: {
      width: "200px",
    },
  },
  activityCardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2.5),
    width: "100%",
    maxWidth: "900px",
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      maxWidth: "95%",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(2),
    },
  },
  activityCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    width: "100%",
    transition: "box-shadow 0.3s, transform 0.3s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      transform: "translateY(-3px)",
    },
  },
  activityIcon: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFF",
    padding: theme.spacing(1.2),
    minWidth: "40px",
    height: "40px",
    borderRadius: "50%",
    marginRight: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  },
  activityContent: {
    textAlign: "left",
    flex: 1,
  },
  activityTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    marginBottom: theme.spacing(0.3),
    color: theme.palette.text.primary,
  },
  activityDescription: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  newTicketButton: {
    borderRadius: "12px",
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 2.5),
    },
  },
  conversationContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    position: "relative",
    overflow: "hidden",
  },
  filterContainer: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  ticketsContainer: {
    flex: 1,
    overflowY: "hidden",
  },
  resetFilterButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.error.light,
    color: "#FFF",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    border: `1px solid ${theme.palette.error.light}`,
    marginLeft: "auto",
    "&:hover": {
      backgroundColor: theme.palette.error.main,
      color: "#FFF",
    },
  },
  fixedDrawerContainer: {
    width: drawerWidth,
    height: "100%",
    flexShrink: 0,
    flexGrow: 0,
    display: "none",
    borderLeft: `0px`,
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
    overflow: "hidden",
    position: "relative",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.up("lg")]: {
      display: "block",
    },
  },
}));

const TicketsModern = () => {
  const { user, socket } = useContext(AuthContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);
  const classes = useStyles();
  const theme = useTheme();
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [searchOnMessages, setSearchOnMessages] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [sortTickets, setSortTickets] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // ✅ CORREÇÃO: Estado para indicar se está buscando
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false); // ✅ CORREÇÃO: Estado para controlar visibilidade do input
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    user.queues?.map((q) => q.id) || []
  );
  const [closeAllModalOpen, setCloseAllModalOpen] = useState(false);
  const searchInputRef = useRef();
  const searchDebounceRef = useRef(null); // ✅ CORREÇÃO: Ref para armazenar o timeout do debounce
  const [isRefreshing, setIsRefreshing] = useState(0);
  const [openUnreadCount, setOpenUnreadCount] = useState(0);
  const [pendingUnreadCount, setPendingUnreadCount] = useState(0);
  const [pendingTotalFromList, setPendingTotalFromList] = useState(0);
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const [isDrawerPinned, setIsDrawerPinned] = useState(() => {
    const saved = localStorage.getItem("contactDrawerPinned");
    return saved === "true";
  });
  const [fixedContact, setFixedContact] = useState(null);
  const [fixedTicket, setFixedTicket] = useState(null);
  const [isLoadingFixedContactData, setIsLoadingFixedContactData] = useState(false);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  useEffect(() => {
    setIsRefreshing((prev) => prev + 1);
  }, [
    user.profile,
    user.allUserChat,
    user.allHistoric,
    user.allowGroup,
    user.allTicket,
    showAllTickets,
  ]);

  // Abrir aba Flow quando vier do ícone Flow da navbar (mobile)
  useEffect(() => {
    if (location.state?.openFlow) {
      setTabOpen("flow");
      history.replace({ ...location, state: {} });
    }
  }, [location.state?.openFlow]);

  useEffect(() => {
    localStorage.setItem("contactDrawerPinned", isDrawerPinned.toString());
    if (!isDrawerPinned) {
      setFixedContact(null);
      setFixedTicket(null);
    }
  }, [isDrawerPinned]);

  useEffect(() => {
    if (isDrawerPinned && ticketId) {
      const fetchTicketDataForDrawer = async () => {
        setIsLoadingFixedContactData(true);
        try {
          const { data } = await api.get("/tickets/u/" + ticketId);
          setFixedContact(data.contact);
          setFixedTicket(data);
        } catch (error) {
          console.error("Failed to fetch ticket data for pinned drawer", error);
        } finally {
          setIsLoadingFixedContactData(false);
        }
      };
      fetchTicketDataForDrawer();
    } else if (isDrawerPinned && !ticketId) {
      setFixedContact(null);
      setFixedTicket(null);
    }
  }, [ticketId, isDrawerPinned]);

  const { ticketCounts, permissions } = useTicketCounts({
    selectedQueueIds,
    showAll: showAllTickets,
    isRefreshing,
  });

  useEffect(() => {
    if (!socket || !user?.companyId) {
      return;
    }

    const companyId = user.companyId;

    const handleTicketEvent = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        setIsRefreshing((prev) => prev + 1);
      }
    };

    // ✅ CORRIGIDO: Removido handleAppMessageEvent que causava looping infinito
    // O evento appMessage já é tratado em outros componentes (MessagesList, etc)
    // e não precisa ser tratado aqui para evitar re-renders desnecessários
    
    socket.on(`company-${companyId}-ticket`, handleTicketEvent);

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicketEvent);
    };
  }, [socket, user, setIsRefreshing]);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    console.log('[SEARCH] handleSearchInputChange - value:', value);
    console.log('[SEARCH] handleSearchInputChange - searchInputValue antes:', searchInputValue);
    console.log('[SEARCH] handleSearchInputChange - searchParam antes:', searchParam);
    console.log('[SEARCH] handleSearchInputChange - isFilterActive:', isFilterActive);
    setSearchInputValue(value);
    setIsSearching(true);

    // ✅ MELHORIA: Limpar timeout anterior se existir
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // ✅ MELHORIA: Debounce de 600ms para busca em tempo real
    // Aumenta para 800ms se o termo for muito curto (< 3 caracteres) para evitar muitas buscas
    const delay = value.trim().length < 3 ? 800 : 600;
    
    searchDebounceRef.current = setTimeout(() => {
      const searchedTerm = value.trim();
      console.log('[SEARCH] Debounce executado - searchedTerm:', searchedTerm);
      setSearchParam(searchedTerm);
      setIsSearching(false);

      if (searchedTerm === "") {
        console.log('[SEARCH] Termo vazio, voltando para tab open e ocultando barra de pesquisa');
        setTabOpen("open");
        // ✅ CORREÇÃO: Ocultar barra de pesquisa quando o campo ficar vazio e não houver outros filtros
        if (
          selectedTags.length === 0 &&
          selectedUsers.length === 0 &&
          selectedWhatsapp.length === 0 &&
          selectedStatus.length === 0
        ) {
          setIsSearchInputVisible(false);
        }
        return;
      }

      // ✅ CORREÇÃO: Não usar "search" como valor de tab, mas sim mostrar resultados na tab "open"
      // O componente TicketsListCustom com status="search" será renderizado quando searchParam existir
      console.log('[SEARCH] Termo encontrado, mantendo tab open e executando busca');
      setTabOpen("open"); // Manter na tab "open" mas mostrar resultados de busca
      setForceSearch(!forceSearch);
    }, delay);
  };

  const executeSearch = () => {
    // ✅ MELHORIA: Limpar debounce se o usuário clicar no botão
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const searchedTerm = searchInputValue.trim();
    console.log('[SEARCH] executeSearch - searchedTerm:', searchedTerm);
    console.log('[SEARCH] executeSearch - searchParam antes:', searchParam);
    setSearchParam(searchedTerm);
    setIsSearching(false);

    if (searchedTerm === "") {
      console.log('[SEARCH] executeSearch - Termo vazio, voltando para tab open');
      setTabOpen("open");
      setIsSearchInputVisible(false);
      return;
    }

    // ✅ CORREÇÃO: Quando há busca, não mudar tabOpen - deixar o componente de busca ser exibido
    console.log('[SEARCH] executeSearch - Termo encontrado, executando busca sem mudar tabOpen');
    // Não mudar tabOpen - o componente de busca será exibido automaticamente quando searchParam existir
    setForceSearch(!forceSearch);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      // ✅ MELHORIA: Limpar debounce ao pressionar Enter
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      executeSearch();
    }
  };

  // ✅ MELHORIA: Limpar timeout ao desmontar componente
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // ✅ CORREÇÃO: Quando há busca ativa, garantir que tabOpen não interfira na exibição
  // Não corrigir tabOpen de "search" para "open" quando há searchParam, pois queremos mostrar a busca
  useEffect(() => {
    // Só corrigir se não houver busca ativa
    if (tabOpen === "search" && !searchParam && !isSearchInputVisible) {
      console.log('[TAB] Corrigindo tabOpen de "search" para "open" (sem busca ativa)');
      setTabOpen("open");
    }
  }, [tabOpen, setTabOpen, searchParam, isSearchInputVisible]);

  // ✅ CORREÇÃO: Mostrar input quando há filtros ativos OU busca ativa
  // Ocultar quando não houver filtros nem busca
  useEffect(() => {
    const hasFilters = selectedTags.length > 0 || selectedUsers.length > 0 || selectedWhatsapp.length > 0 || selectedStatus.length > 0;
    const hasSearch = searchParam && searchParam.trim();
    const hasInputValue = searchInputValue && searchInputValue.trim();
    
    if (hasFilters || hasSearch || hasInputValue) {
      setIsSearchInputVisible(true);
    } else {
      // ✅ CORREÇÃO: Ocultar barra quando não houver filtros nem busca
      setIsSearchInputVisible(false);
    }
  }, [selectedTags, selectedUsers, selectedWhatsapp, selectedStatus, searchParam, searchInputValue]);

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchParam("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    if (
      selectedTags.length === 0 &&
      selectedUsers.length === 0 &&
      selectedWhatsapp.length === 0 &&
      selectedStatus.length === 0
    ) {
      setTabOpen("open");
      setIsSearchInputVisible(false); // ✅ CORREÇÃO: Ocultar input quando não há filtros
    }
  };

  const handleFilterToggle = () => {
    const newFilterState = !isFilterActive;
    console.log('[FILTER] handleFilterToggle - newFilterState:', newFilterState);
    console.log('[FILTER] handleFilterToggle - isFilterActive antes:', isFilterActive);
    console.log('[FILTER] handleFilterToggle - tabOpen:', tabOpen);
    console.log('[FILTER] handleFilterToggle - searchParam:', searchParam);
    setIsFilterActive(newFilterState);

    if (newFilterState && tabOpen !== "search") {
      console.log('[FILTER] Abrindo filtros, mudando para tab open');
      setTabOpen("open");
    } else if (
      !newFilterState &&
      searchParam === "" &&
      selectedTags.length === 0 &&
      selectedUsers.length === 0 &&
      selectedWhatsapp.length === 0 &&
      selectedStatus.length === 0
    ) {
      console.log('[FILTER] Fechando filtros sem busca, voltando para tab open');
      setTabOpen("open");
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);

    if (tags.length > 0 && tabOpen !== "open") {
      setTabOpen("open"); // ✅ CORREÇÃO: Não usar "search" como tab
    } else if (
      tags.length === 0 &&
      searchParam === "" &&
      selectedUsers.length === 0 &&
      selectedWhatsapp.length === 0 &&
      selectedStatus.length === 0
    ) {
      setTabOpen("open");
    }

    setForceSearch(!forceSearch);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);

    if (users.length > 0) {
      if (users.includes(user.id) && users.length === 1) {
        setShowAllTickets(false);
      } else {
        setShowAllTickets(true);
      }

      if (tabOpen !== "open") {
        setTabOpen("open"); // ✅ CORREÇÃO: Não usar "search" como tab
      }
    } else {
      setShowAllTickets(false);

      if (
        searchParam === "" &&
        selectedTags.length === 0 &&
        selectedWhatsapp.length === 0 &&
        selectedStatus.length === 0
      ) {
        setTabOpen("open");
      }
    }

    setForceSearch(!forceSearch);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    setSelectedWhatsapp(whatsapp);

    if (whatsapp.length > 0 && tabOpen !== "open") {
      setTabOpen("open"); // ✅ CORREÇÃO: Não usar "search" como tab
    } else if (
      whatsapp.length === 0 &&
      searchParam === "" &&
      selectedTags.length === 0 &&
      selectedUsers.length === 0 &&
      selectedStatus.length === 0
    ) {
      setTabOpen("open");
    }

    setForceSearch(!forceSearch);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);
    setSelectedStatus(statusFilter);

    if (statusFilter.length > 0 && tabOpen !== "open") {
      setTabOpen("open"); // ✅ CORREÇÃO: Não usar "search" como tab
    } else if (
      statusFilter.length === 0 &&
      searchParam === "" &&
      selectedTags.length === 0 &&
      selectedUsers.length === 0 &&
      selectedWhatsapp.length === 0
    ) {
      setTabOpen("open");
    }

    setForceSearch(!forceSearch);
  };

  const resetAllFilters = () => {
    setSelectedTags([]);
    setSelectedUsers([]);
    setSelectedWhatsapp([]);
    setSelectedStatus([]);
    setSearchParam("");
    setSearchInputValue("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setTabOpen("open");
    setForceSearch(!forceSearch);
    setIsFilterActive(false);
  };

  const handleCloseAllTickets = () => {
    setCloseAllModalOpen(true);
  };

  const CloseAllTicket = async () => {
    try {
      await api.post("/tickets/closeAll", {
        status: tabOpen,
        selectedQueueIds,
      });
      setCloseAllModalOpen(false);
      setIsRefreshing((prev) => prev + 1);
    } catch (err) {
      console.log("Error closing tickets:", err);
    }
  };

  const handleToggleShowAll = () => {
    if (
      permissions.isAdmin ||
      permissions.canSeeAllTickets ||
      permissions.canSeeAllUserChats ||
      permissions.canSeeAllHistory
    ) {
      setShowAllTickets(!showAllTickets);
    } else {
      setShowAllTickets(false);
    }
  };

  const renderSidebarButton = (icon, label, value, count = 0, unreadCount = 0) => {
    const Icon = icon;
    const numCount = Number(count) || 0;
    const numUnread = Number(unreadCount) || 0;
    const showBadge = numUnread > 0 || numCount > 0;
    const badgeValue = numUnread > 0 ? numUnread : numCount;
    const handleClick = () => {
      if (value === "search") {
        setIsSearchInputVisible(true);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 100);
      } else {
        setTabOpen(value);
      }
    };

    const isActive = value === "search"
      ? isSearchInputVisible
      : tabOpen === value;

    return (
      <div
        className={`${classes.sidebarButton} ${isActive ? "active" : ""}`}
        onClick={handleClick}
      >
        <Icon size={22} />
        {showBadge && (
          <div
            className={classes.badgeCount}
            style={{
              backgroundColor: numUnread > 0 ? "#f44336" : "#4caf50",
            }}
          >
            {badgeValue}
          </div>
        )}
      </div>
    );
  };

  const handleContactDrawerUnpin = () => {
    setIsDrawerPinned(false);
    localStorage.setItem("contactDrawerPinned", "false");
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      setTabOpen("open");
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  return (
    <ModernLayoutProvider>
      <QueueSelectedProvider>
      <div className={classes.chatContainer}>
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseOrOpenTicket}
        />

        <CloseAllTicketsModal
          open={closeAllModalOpen}
          onClose={() => setCloseAllModalOpen(false)}
          onConfirm={CloseAllTicket}
        />

        <div
          className={clsx(classes.chatPapper, {
            [classes.chatPapperWithDrawer]:
              isDrawerPinned && isLargeScreen && ticketId,
          })}
        >
          <div className={classes.contactsWrapper}>
            <div className={classes.sidebar}>
              <div
                className={classes.sidebarAddButton}
                onClick={() => setNewTicketModalOpen(true)}
              >
                <Tooltip title={i18n.t("ticketsList.actions.newTicket")} placement="right" arrow>
                  <div className={classes.sidebarButton}>
                    <PlusCircle size={22} />
                  </div>
                </Tooltip>
              </div>

              <div className={classes.sidebarButtonsContainer}>
                <Tooltip
                  title={i18n.t("ticketsList.assignedHeader")}
                  placement="right"
                  arrow
                >
                  {renderSidebarButton(
                    MessageSquare,
                    i18n.t("ticketsList.assignedHeader"),
                    "open",
                    ticketCounts.open,
                    openUnreadCount
                  )}
                </Tooltip>

                <Tooltip
                  title={i18n.t("ticketsList.pendingHeader")}
                  placement="right"
                  arrow
                >
                  {renderSidebarButton(
                    Clock,
                    i18n.t("ticketsList.pendingHeader"),
                    "pending",
                    pendingTotalFromList > 0 ? pendingTotalFromList : ticketCounts.pending,
                    pendingUnreadCount
                  )}
                </Tooltip>

                {user.allowGroup && (
                  <Tooltip
                    title={i18n.t("ticketsList.groupingHeader")}
                    placement="right"
                    arrow
                  >
                    {renderSidebarButton(
                      Users,
                      i18n.t("ticketsList.groupingHeader"),
                      "group",
                      ticketCounts.group,
                      groupUnreadCount
                    )}
                  </Tooltip>
                )}

                <Tooltip
                  title={i18n.t("ticketsList.closedHeader")}
                  placement="right"
                  arrow
                >
                  {renderSidebarButton(
                    Archive,
                    i18n.t("ticketsList.closedHeader"),
                    "closed"
                  )}
                </Tooltip>

                <Tooltip
                  title={i18n.t("ticketsList.flowHeader")}
                  placement="right"
                  arrow
                >
                  {renderSidebarButton(
                    GitBranch,
                    i18n.t("ticketsList.flowHeader"),
                    "flow",
                    ticketCounts.flow
                  )}
                </Tooltip>

                <Tooltip title={i18n.t("ticketsList.actions.search")} placement="right" arrow>
                  {renderSidebarButton(Search, i18n.t("ticketsList.actions.search"), "search")}
                </Tooltip>
              </div>
            </div>

            <div className={classes.ticketsSection}>
              {/* Mobile Controls */}
              <div className={classes.mobileControlsBar}>
                <div className={classes.mobileControlsRow}>
                  <div className={classes.mobileControlButtons}>
                    <Can
                      role={
                        (user.allUserChat === "enabled" ||
                          user.allHistoric === "enabled") &&
                        user.profile === "user"
                          ? "admin"
                          : user.profile
                      }
                      perform="tickets-manager:showall"
                      yes={() => (
                        <div
                          className={`${classes.controlButton} ${showAllTickets ? "active" : ""}`}
                          onClick={handleToggleShowAll}
                        >
                          {showAllTickets ? <Eye size={16} /> : <EyeOff size={16} />}
                        </div>
                      )}
                    />
                    <div
                      className={classes.controlButton}
                      onClick={() => setNewTicketModalOpen(true)}
                    >
                      <PlusCircle size={16} />
                    </div>
                    <Tooltip title={i18n.t("ticketsList.flowHeader") || "Flow"} placement="bottom" arrow>
                      <IconButton
                        size="small"
                        className={`${classes.controlButton} ${tabOpen === "flow" ? "active" : ""}`}
                        onClick={() => setTabOpen("flow")}
                        aria-label={i18n.t("ticketsList.flowHeader") || "Flow"}
                        style={{ padding: 6 }}
                      >
                        <Badge
                          overlap="circular"
                          max={999}
                          badgeContent={ticketCounts.flow}
                          color="primary"
                          invisible={ticketCounts.flow === 0}
                        >
                          <AccountTreeIcon style={{ fontSize: 20 }} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <div
                      className={`${classes.controlButton} ${isFilterActive ? "active" : ""}`}
                      onClick={handleFilterToggle}
                    >
                      <Filter size={16} />
                    </div>
                    <div
                      className={`${classes.controlButton} ${sortTickets ? "active" : ""}`}
                      onClick={() => setSortTickets(!sortTickets)}
                    >
                      {sortTickets ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <TicketsQueueSelect
                      selectedQueueIds={selectedQueueIds}
                      userQueues={user?.queues}
                      onChange={setSelectedQueueIds}
                    />
                  </div>
                </div>
              </div>

              {/* Barra horizontal no mobile com os mesmos ícones da sidebar (incluindo Flow) */}
              <div className={classes.mobileSidebarBar}>
                <Tooltip title={i18n.t("ticketsList.assignedHeader")} placement="bottom" arrow>
                  <div
                    className={`${classes.mobileSidebarButton} ${tabOpen === "open" ? "active" : ""}`}
                    onClick={() => setTabOpen("open")}
                  >
                    <MessageSquare size={20} />
                    {(openUnreadCount > 0 ? openUnreadCount : ticketCounts.open) > 0 && (
                      <span className={classes.badgeCount} style={{ backgroundColor: openUnreadCount > 0 ? "#f44336" : "#4caf50" }}>
                        {openUnreadCount > 0 ? openUnreadCount : ticketCounts.open}
                      </span>
                    )}
                  </div>
                </Tooltip>
                <Tooltip title={i18n.t("ticketsList.pendingHeader")} placement="bottom" arrow>
                  <div
                    className={`${classes.mobileSidebarButton} ${tabOpen === "pending" ? "active" : ""}`}
                    onClick={() => setTabOpen("pending")}
                  >
                    <Clock size={20} />
                    {(pendingUnreadCount > 0 ? pendingUnreadCount : ticketCounts.pending) > 0 && (
                      <span className={classes.badgeCount} style={{ backgroundColor: pendingUnreadCount > 0 ? "#f44336" : "#4caf50" }}>
                        {pendingUnreadCount > 0 ? pendingUnreadCount : ticketCounts.pending}
                      </span>
                    )}
                  </div>
                </Tooltip>
                {user.allowGroup && (
                  <Tooltip title={i18n.t("ticketsList.groupingHeader")} placement="bottom" arrow>
                    <div
                      className={`${classes.mobileSidebarButton} ${tabOpen === "group" ? "active" : ""}`}
                      onClick={() => setTabOpen("group")}
                    >
                      <Users size={20} />
                      {(groupUnreadCount > 0 ? groupUnreadCount : ticketCounts.group) > 0 && (
                        <span className={classes.badgeCount} style={{ backgroundColor: groupUnreadCount > 0 ? "#f44336" : "#4caf50" }}>
                          {groupUnreadCount > 0 ? groupUnreadCount : ticketCounts.group}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                )}
                <Tooltip title={i18n.t("ticketsList.flowHeader") || "Flow"} placement="bottom" arrow>
                  <div
                    className={`${classes.mobileSidebarButton} ${tabOpen === "flow" ? "active" : ""}`}
                    onClick={() => setTabOpen("flow")}
                    role="button"
                    aria-label="Flow"
                  >
                    {ticketCounts.flow > 0 ? (
                      <Badge badgeContent={ticketCounts.flow} color="primary" overlap="circular">
                        <AccountTreeIcon style={{ fontSize: 20 }} />
                      </Badge>
                    ) : (
                      <AccountTreeIcon style={{ fontSize: 20 }} />
                    )}
                  </div>
                </Tooltip>
                <Tooltip title={i18n.t("ticketsList.closedHeader")} placement="bottom" arrow>
                  <div
                    className={`${classes.mobileSidebarButton} ${tabOpen === "closed" ? "active" : ""}`}
                    onClick={() => setTabOpen("closed")}
                  >
                    <Archive size={20} />
                  </div>
                </Tooltip>
                <Tooltip title={i18n.t("ticketsList.actions.search")} placement="bottom" arrow>
                  <div
                    className={`${classes.mobileSidebarButton} ${isSearchInputVisible ? "active" : ""}`}
                    onClick={() => {
                      setIsSearchInputVisible(true);
                      setTimeout(() => searchInputRef.current?.focus(), 100);
                    }}
                  >
                    <Search size={20} />
                  </div>
                </Tooltip>
              </div>

              {/* Mobile Tabs (igual layout antigo) */}
              <Tabs
                value={tabOpen || "open"}
                onChange={(e, newValue) => setTabOpen(newValue)}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                scrollButtons="auto"
                TabIndicatorProps={{ className: classes.mobileTabIndicator }}
                className={classes.mobileTabs}
              >
                {/* ATENDENDO */}
                <Tab
                  label={
                    <Grid container alignItems="center" justifyContent="center" style={{ position: "relative", paddingTop: 10 }}>
                      <Grid item>
                        <Badge
                          overlap="circular"
                          max={999}
                          classes={{ badge: openUnreadCount > 0 ? classes.mobileCustomBadgeRed : classes.mobileCustomBadge }}
                          badgeContent={openUnreadCount > 0 ? openUnreadCount : ticketCounts.open}
                          color="primary"
                        >
                          <MessageSharpIcon
                            style={{
                              fontSize: 20,
                              color: tabOpen === "open" ? theme.palette.primary.main : "inherit",
                            }}
                          />
                        </Badge>
                      </Grid>
                      <Grid item>
                        <Typography
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: tabOpen === "open" ? 700 : 500,
                            color: tabOpen === "open" ? theme.palette.primary.main : "inherit",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {i18n.t("ticketsList.assignedHeader")}
                        </Typography>
                      </Grid>
                    </Grid>
                  }
                  value="open"
                  classes={{ root: classes.mobileTabPanelItem }}
                />

                {/* AGUARDANDO */}
                <Tab
                  label={
                    <Grid container alignItems="center" justifyContent="center" style={{ position: "relative", paddingTop: 10 }}>
                      <Grid item>
                        <Badge
                          overlap="circular"
                          max={999}
                          classes={{ badge: pendingUnreadCount > 0 ? classes.mobileCustomBadgeRed : classes.mobileCustomBadge }}
                          badgeContent={pendingUnreadCount > 0 ? pendingUnreadCount : ticketCounts.pending}
                          color="primary"
                        >
                          <ClockIcon
                            style={{
                              fontSize: 20,
                              color: tabOpen === "pending" ? theme.palette.primary.main : "inherit",
                            }}
                          />
                        </Badge>
                      </Grid>
                      <Grid item>
                        <Typography
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: tabOpen === "pending" ? 700 : 500,
                            color: tabOpen === "pending" ? theme.palette.primary.main : "inherit",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {i18n.t("ticketsList.pendingHeader")}
                        </Typography>
                      </Grid>
                    </Grid>
                  }
                  value="pending"
                  classes={{ root: classes.mobileTabPanelItem }}
                />

                {/* GRUPOS */}
                {user.allowGroup && (
                  <Tab
                    label={
                      <Grid container alignItems="center" justifyContent="center" style={{ position: "relative", paddingTop: 10 }}>
                        <Grid item>
                          <Badge
                            overlap="circular"
                            max={999}
                            classes={{ badge: groupUnreadCount > 0 ? classes.mobileCustomBadgeRed : classes.mobileCustomBadge }}
                            badgeContent={groupUnreadCount > 0 ? groupUnreadCount : ticketCounts.group}
                            color="primary"
                          >
                            <GroupIcon
                              style={{
                                fontSize: 20,
                                color: tabOpen === "group" ? theme.palette.primary.main : "inherit",
                              }}
                            />
                          </Badge>
                        </Grid>
                        <Grid item>
                          <Typography
                            style={{
                              marginLeft: 8,
                              fontSize: 11,
                              fontWeight: tabOpen === "group" ? 700 : 500,
                              color: tabOpen === "group" ? theme.palette.primary.main : "inherit",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {i18n.t("ticketsList.groupingHeader")}
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                    value="group"
                    classes={{ root: classes.mobileTabPanelItem }}
                  />
                )}

                {/* FLOW */}
                <Tab
                  label={
                    <Grid container alignItems="center" justifyContent="center" style={{ position: "relative", paddingTop: 10 }}>
                      <Grid item>
                        <Badge
                          overlap="circular"
                          max={999}
                          classes={{ badge: classes.mobileCustomBadge }}
                          badgeContent={ticketCounts.flow}
                          color="primary"
                        >
                          <AccountTreeIcon
                            style={{
                              fontSize: 20,
                              color: tabOpen === "flow" ? theme.palette.primary.main : "inherit",
                            }}
                          />
                        </Badge>
                      </Grid>
                      <Grid item>
                        <Typography
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: tabOpen === "flow" ? 700 : 500,
                            color: tabOpen === "flow" ? theme.palette.primary.main : "inherit",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {i18n.t("ticketsList.flowHeader")}
                        </Typography>
                      </Grid>
                    </Grid>
                  }
                  value="flow"
                  classes={{ root: classes.mobileTabPanelItem }}
                />
              </Tabs>

              {/* Desktop Controls */}
              <div className={`${classes.controlsBar} ${classes.desktopControlsBar}`}>
                <div className={classes.controlsTopRow}>
                  <div className={classes.controlButtons}>
                    <Can
                      role={
                        (user.allUserChat === "enabled" ||
                          user.allHistoric === "enabled") &&
                        user.profile === "user"
                          ? "admin"
                          : user.profile
                      }
                      perform="tickets-manager:showall"
                      yes={() => (
                        <Tooltip
                          title={
                            showAllTickets
                              ? i18n.t("ticketsList.actions.showMyTickets")
                              : i18n.t("ticketsList.actions.showAll")
                          }
                          placement="top"
                          arrow
                        >
                          <div
                            className={`${classes.controlButton} ${
                              showAllTickets ? "active" : ""
                            }`}
                            onClick={handleToggleShowAll}
                          >
                            {showAllTickets ? (
                              <Eye size={18} />
                            ) : (
                              <EyeOff size={18} />
                            )}
                          </div>
                        </Tooltip>
                      )}
                    />

                    <Tooltip
                      title={
                        isFilterActive
                          ? i18n.t("ticketsList.actions.closeFilters")
                          : i18n.t("ticketsList.actions.openFilters")
                      }
                      placement="top"
                      arrow
                    >
                      <div
                        className={`${classes.controlButton} ${
                          isFilterActive ? "active" : ""
                        }`}
                        onClick={handleFilterToggle}
                      >
                        <Filter size={18} />
                      </div>
                    </Tooltip>

                    <Tooltip
                      title={
                        sortTickets
                          ? i18n.t("ticketsList.actions.newestFirst")
                          : i18n.t("ticketsList.actions.oldestFirst")
                      }
                      placement="top"
                      arrow
                    >
                      <div
                        className={`${classes.controlButton} ${
                          sortTickets ? "active" : ""
                        }`}
                        onClick={() => setSortTickets(!sortTickets)}
                      >
                        {sortTickets ? (
                          <ArrowUp size={18} />
                        ) : (
                          <ArrowDown size={18} />
                        )}
                      </div>
                    </Tooltip>

                    {user.profile === "admin" && tabOpen !== "closed" && (
                      <Tooltip
                        title={i18n.t("ticketsList.actions.closeAllTickets")}
                        placement="top"
                        arrow
                      >
                        <div
                          className={classes.controlButton}
                          onClick={handleCloseAllTickets}
                        >
                          <CheckCircle2 size={18} />
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  <div className={classes.queueSelect}>
                    <TicketsQueueSelect
                      selectedQueueIds={selectedQueueIds}
                      userQueues={user?.queues}
                      onChange={setSelectedQueueIds}
                    />
                  </div>
                </div>

                {/* ✅ CORREÇÃO: Manter input de pesquisa aberto quando houver busca ativa ou filtros ativos */}
                {(() => {
                  // ✅ CORREÇÃO: Input deve aparecer se houver busca, valor no input, filtros ativos, ou se o estado isSearchInputVisible estiver true
                  const hasFilters = selectedTags.length > 0 || selectedUsers.length > 0 || selectedWhatsapp.length > 0 || selectedStatus.length > 0;
                  const collapseIn = (searchParam && searchParam.trim()) || searchInputValue || isFilterActive || hasFilters || isSearchInputVisible;
                  console.log('[COLLAPSE] Renderizando - collapseIn:', collapseIn);
                  console.log('[COLLAPSE] - searchParam:', searchParam, 'trimmed:', searchParam ? searchParam.trim() : 'null');
                  console.log('[COLLAPSE] - searchInputValue:', searchInputValue);
                  console.log('[COLLAPSE] - isFilterActive:', isFilterActive);
                  console.log('[COLLAPSE] - hasFilters:', hasFilters);
                  console.log('[COLLAPSE] - isSearchInputVisible:', isSearchInputVisible);
                  console.log('[COLLAPSE] - tabOpen:', tabOpen);
                  return (
                    <Collapse in={collapseIn}>
                      <div className={classes.searchInputWrapper}>
                    <Search size={18} />
                    <input
                      className={classes.searchInput}
                      placeholder={i18n.t("tickets.search.placeholder")}
                      value={searchInputValue}
                      onChange={handleSearchInputChange}
                      onKeyPress={handleSearchKeyPress}
                      ref={searchInputRef}
                    />
                    <Tooltip title={i18n.t("ticketsList.actions.searchButton")} placement="top" arrow>
                      <div
                        className={classes.controlButton}
                        onClick={executeSearch}
                      >
                        <Search size={14} />
                      </div>
                    </Tooltip>
                    {(searchInputValue || searchParam) && (
                      <Tooltip
                        title={i18n.t("ticketsList.actions.clearSearch")}
                        placement="top"
                        arrow
                      >
                        <div
                          className={classes.controlButton}
                          onClick={handleClearSearch}
                        >
                          <XCircle size={14} />
                        </div>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={i18n.t("ticketsList.actions.searchInMessages")}
                      placement="top"
                      arrow
                    >
                      <div
                        className={`${classes.controlButton} ${
                          searchOnMessages ? "active" : ""
                        }`}
                        onClick={() => {
                          setSearchOnMessages(!searchOnMessages);
                          if (searchParam && tabOpen === "search") {
                            setForceSearch(!forceSearch);
                          }
                        }}
                      >
                        {searchOnMessages ? (
                          <FileText size={14} />
                        ) : (
                          <File size={14} />
                        )}
                      </div>
                    </Tooltip>
                  </div>
                    </Collapse>
                  );
                })()}
              </div>

              {isFilterActive && (
                <div className={classes.filterContainer}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: theme.spacing(1),
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {i18n.t("ticketsList.actions.appliedFilters")}
                    </span>
                    <div
                      className={classes.resetFilterButton}
                      onClick={resetAllFilters}
                      title={i18n.t("ticketsList.actions.clearAllFilters")}
                    >
                      <XCircle size={14} />
                      <span>{i18n.t("ticketsList.actions.clearFilters")}</span>
                    </div>
                  </div>
                  <div className={classes.filterRow}>
                    <div style={{ flex: 1 }}>
                      <TagsFilter onFiltered={handleSelectedTags} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
                    </div>
                  </div>
                  <div className={classes.filterRow}>
                    <div style={{ flex: 1 }}>
                      <StatusFilter onFiltered={handleSelectedStatus} />
                    </div>
                    {user.profile === "admin" && (
                      <div style={{ flex: 1 }}>
                        <UsersFilter onFiltered={handleSelectedUsers} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className={classes.ticketsContainer}>
                {/* ✅ CORREÇÃO: Só mostrar lista "open" se não houver busca ativa OU filtros */}
                {(() => {
                  const hasFilters = selectedTags.length > 0 || selectedUsers.length > 0 || selectedWhatsapp.length > 0 || selectedStatus.length > 0;
                  const hasSearchParam = searchParam && searchParam.trim();
                  
                  // Se há filtros ou busca, não renderizar o componente "open"
                  if (hasFilters || hasSearchParam) {
                    return null;
                  }
                  
                  return (
                      <TicketsListCustom
                        key={`open-${selectedQueueIds.join(
                          "-"
                        )}-${showAllTickets}-${sortTickets}-${searchOnMessages}-${searchParam || ''}`}
                        status="open"
                        showAll={showAllTickets}
                        sortTickets={sortTickets ? "ASC" : "DESC"}
                        selectedQueueIds={selectedQueueIds}
                        searchParam={searchParam || ""}
                        searchOnMessages={searchOnMessages}
                        setTabOpen={setTabOpen}
                        updateUnreadCount={setOpenUnreadCount}
                        // ✅ CORREÇÃO: Ocultar componente "open" quando há busca ou filtros ativos
                        style={
                          (searchParam && searchParam.trim()) || 
                          selectedTags.length > 0 || 
                          selectedUsers.length > 0 || 
                          selectedWhatsapp.length > 0 || 
                          selectedStatus.length > 0 ||
                          tabOpen !== "open"
                            ? { width: 0, height: 0, overflow: "hidden" }
                            : undefined
                        }
                        onTicketStatusChange={() =>
                          setIsRefreshing((prev) => prev + 1)
                        }
                      />
                  );
                })()}

                {/* ✅ CORREÇÃO: Só renderizar componentes de status se não houver filtros ativos OU busca */}
                {(() => {
                  const hasFilters = selectedTags.length > 0 || selectedUsers.length > 0 || selectedWhatsapp.length > 0 || selectedStatus.length > 0;
                  const hasSearchParam = searchParam && searchParam.trim();
                  
                  // Se há filtros ou busca, não renderizar os componentes de status (open, pending, group, closed)
                  if (hasFilters || hasSearchParam) {
                    return null;
                  }
                  
                  return (
                    <>
                      <TicketsListCustom
                        key={`pending-${selectedQueueIds.join(
                          "-"
                        )}-${showAllTickets}-${sortTickets}-${searchOnMessages}-${searchParam || ''}`}
                        status="pending"
                        selectedQueueIds={selectedQueueIds}
                        sortTickets={sortTickets ? "ASC" : "DESC"}
                        searchParam={searchParam || ""}
                        searchOnMessages={searchOnMessages}
                        showAll={
                          user.profile === "admin" ||
                          user.allUserChat === "enabled" ||
                          user.allHistoric === "enabled"
                            ? showAllTickets
                            : false
                        }
                        setTabOpen={setTabOpen}
                        updateUnreadCount={setPendingUnreadCount}
                        updateTotalCount={setPendingTotalFromList}
                        style={tabOpen !== "pending" ? { width: 0, height: 0, overflow: "hidden" } : undefined}
                        onTicketStatusChange={() =>
                          setIsRefreshing((prev) => prev + 1)
                        }
                      />

                      {user.allowGroup && (
                        <TicketsListCustom
                          key={`group-${selectedQueueIds.join(
                            "-"
                          )}-${showAllTickets}-${sortTickets}-${searchOnMessages}-${searchParam || ''}`}
                          status="group"
                          showAll={showAllTickets}
                          sortTickets={sortTickets ? "ASC" : "DESC"}
                          selectedQueueIds={selectedQueueIds}
                          searchParam={searchParam || ""}
                          searchOnMessages={searchOnMessages}
                          setTabOpen={setTabOpen}
                          updateUnreadCount={setGroupUnreadCount}
                          style={tabOpen !== "group" ? { width: 0, height: 0, overflow: "hidden" } : undefined}
                          onTicketStatusChange={() =>
                            setIsRefreshing((prev) => prev + 1)
                          }
                        />
                      )}

                      {tabOpen === "closed" && (
                        <TicketsListCustom
                          key={`closed-${selectedQueueIds.join(
                            "-"
                          )}-${showAllTickets}-${searchOnMessages}-${searchParam || ''}`}
                          status="closed"
                          showAll={showAllTickets}
                          selectedQueueIds={selectedQueueIds}
                          searchParam={searchParam || ""}
                          searchOnMessages={searchOnMessages}
                          setTabOpen={setTabOpen}
                        />
                      )}

                      {tabOpen === "flow" && (
                        <TicketsListCustom
                          key={`flow-${selectedQueueIds.join(
                            "-"
                          )}-${showAllTickets}-${sortTickets}-${searchOnMessages}-${searchParam || ''}`}
                          status="flow"
                          showAll={showAllTickets}
                          sortTickets={sortTickets ? "ASC" : "DESC"}
                          selectedQueueIds={selectedQueueIds}
                          searchParam={searchParam || ""}
                          searchOnMessages={searchOnMessages}
                          setTabOpen={setTabOpen}
                          style={tabOpen !== "flow" ? { width: 0, height: 0, overflow: "hidden" } : undefined}
                          onTicketStatusChange={() =>
                            setIsRefreshing((prev) => prev + 1)
                          }
                        />
                      )}
                    </>
                  );
                })()}

                {/* ✅ CORREÇÃO: Mostrar resultados de busca quando searchParam existir OU quando há filtros selecionados (tags, users, whatsapp, status) */}
                {(() => {
                  // ✅ CORREÇÃO: Garantir que searchParam sempre seja uma string
                  const safeSearchParam = searchParam || "";
                  const hasSearchParam = safeSearchParam && safeSearchParam.trim();
                  const hasFilters = selectedTags.length > 0 || selectedUsers.length > 0 || selectedWhatsapp.length > 0 || selectedStatus.length > 0;
                  const shouldShowSearch = hasSearchParam || hasFilters;
                  
                  console.log('[RENDER-SEARCH] Verificando renderização do componente de busca');
                  console.log('[RENDER-SEARCH] - searchParam:', searchParam);
                  console.log('[RENDER-SEARCH] - safeSearchParam:', safeSearchParam);
                  console.log('[RENDER-SEARCH] - hasSearchParam:', hasSearchParam);
                  console.log('[RENDER-SEARCH] - selectedTags:', selectedTags);
                  console.log('[RENDER-SEARCH] - selectedUsers:', selectedUsers);
                  console.log('[RENDER-SEARCH] - selectedWhatsapp:', selectedWhatsapp);
                  console.log('[RENDER-SEARCH] - selectedStatus:', selectedStatus);
                  console.log('[RENDER-SEARCH] - hasFilters:', hasFilters);
                  console.log('[RENDER-SEARCH] - shouldShowSearch:', shouldShowSearch);
                  console.log('[RENDER-SEARCH] - tabOpen:', tabOpen);
                  
                  if (shouldShowSearch) {
                    console.log('[RENDER-SEARCH] ✅ Renderizando componente de busca');
                    // ✅ CORREÇÃO: Quando há busca, sempre mostrar o componente (não ocultar baseado em tabOpen)
                    return (
                      <TicketsListCustom
                        key={`search-${selectedQueueIds.join(
                          "-"
                        )}-${selectedTags.join("-")}-${selectedUsers.join(
                          "-"
                        )}-${selectedWhatsapp.join("-")}-${selectedStatus.join(
                          "-"
                        )}-${showAllTickets}-${sortTickets}-${searchOnMessages}-${safeSearchParam}-${forceSearch}`}
                        statusFilter={selectedStatus}
                        searchParam={safeSearchParam}
                        showAll={showAllTickets}
                        tags={selectedTags}
                        users={selectedUsers}
                        selectedQueueIds={selectedQueueIds}
                        whatsappIds={selectedWhatsapp}
                        forceSearch={forceSearch}
                        searchOnMessages={searchOnMessages}
                        status="search"
                        setTabOpen={setTabOpen}
                        // ✅ CORREÇÃO: Não ocultar quando há busca ativa - sempre mostrar
                        style={undefined}
                      />
                    );
                  } else {
                    console.log('[RENDER-SEARCH] ❌ NÃO renderizando componente de busca');
                    return null;
                  }
                })()}
                
                {/* ✅ LOG: Adicionar log para debug */}
                {(() => {
                  console.log('[RENDER] Renderizando tickets - searchParam:', searchParam, 'tabOpen:', tabOpen);
                  console.log('[RENDER] Componente open será renderizado:', (!searchParam || !searchParam.trim()));
                  console.log('[RENDER] Componente search será renderizado:', (searchParam && searchParam.trim()));
                  return null;
                })()}
              </div>
            </div>
          </div>
          <div
            className={clsx(classes.messagesWrapper, {
              [classes.messagesWrapperWithDrawer]:
                isDrawerPinned && isLargeScreen && ticketId,
              [classes.messagesWrapperHidden]:
                !ticketId && theme.breakpoints.down("sm"),
            })}
          >
            {ticketId ? (
              <div className={classes.conversationContainer}>
                <Ticket />
              </div>
            ) : (
              <Paper elevation={0} className={classes.welcomeMsg}>
                <img
                  className={classes.logo}
                  alt="Logo"
                />

                <Typography variant="h5" className={classes.welcomeTitle}>
                  {i18n.t("chat.noTicketMessage")}
                </Typography>

                <Typography
                  variant="body1"
                  className={classes.welcomeDescription}
                >
                  Selecione um ticket à esquerda ou crie um novo para iniciar.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  className={classes.newTicketButton}
                  startIcon={<PlusCircle size={20} />}
                  onClick={() => setNewTicketModalOpen(true)}
                >
                  {i18n.t("ticketsList.actions.newAttendance")}
                </Button>

                <div className={classes.activityCardsContainer}>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <MessageSquare size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Omnichannel
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Múltiplas filas e usuários simultâneos.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <Users size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Escalável
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Arquitetura pronta para crescer.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <Zap size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Automação
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Respostas rápidas e regras automáticas.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <BarChart2 size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Monitoramento
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Métricas no dashboard.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <GitBranch size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Colaboração
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Transfira tickets facilmente.
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.activityCard}>
                    <div className={classes.activityIcon}>
                      <ShieldCheck size={20} />
                    </div>
                    <div className={classes.activityContent}>
                      <Typography className={classes.activityTitle}>
                        Segurança
                      </Typography>
                      <Typography className={classes.activityDescription}>
                        Controle de acesso completo.
                      </Typography>
                    </div>
                  </div>
                </div>
              </Paper>
            )}
          </div>

          {isDrawerPinned && isLargeScreen && ticketId && (
            <div
              className={classes.fixedDrawerContainer}
              style={{ width: drawerWidth }}
            >
              <ContactDrawer
                open={true}
                handleDrawerClose={handleContactDrawerUnpin}
                contact={fixedContact}
                ticket={fixedTicket}
                loading={isLoadingFixedContactData}
              />
            </div>
          )}
        </div>
      </div>
    </QueueSelectedProvider>
    </ModernLayoutProvider>
  );
};

export default TicketsModern;

